"use client";

import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { SearchIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Command Component
 *
 * A command palette component for searching and executing commands.
 * Built on top of cmdk with shadcn/ui styling.
 *
 * @example
 * ```tsx
 * <Command>
 *   <CommandInput placeholder="Search..." />
 *   <CommandList>
 *     <CommandGroup heading="Actions">
 *       <CommandItem>New File</CommandItem>
 *       <CommandItem>Open Settings</CommandItem>
 *     </CommandGroup>
 *   </CommandList>
 * </Command>
 * ```
 */
function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
    return (
        <CommandPrimitive
            data-slot="command"
            className={cn(
                "setu:bg-popover setu:text-popover-foreground setu:flex setu:h-full setu:w-full setu:flex-col setu:overflow-hidden setu:rounded-md",
                className
            )}
            {...props}
        />
    );
}
Command.displayName = "Command";

/**
 * CommandDialog Component
 *
 * A dialog wrapper for the command palette.
 */
function CommandDialog({
    title = "Command Palette",
    description = "Search for a command to run...",
    children,
    className,
    showCloseButton = true,
    ...props
}: React.ComponentProps<typeof Dialog> & {
    title?: string;
    description?: string;
    className?: string;
    showCloseButton?: boolean;
}) {
    return (
        <Dialog {...props}>
            <DialogHeader className="setu:sr-only">
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>{description}</DialogDescription>
            </DialogHeader>
            <DialogContent className={cn("setu:overflow-hidden setu:p-0", className)} showCloseButton={showCloseButton}>
                <Command className="setu:[&_[cmdk-group-heading]]:text-muted-foreground setu:**:data-[slot=command-input-wrapper]:h-12 setu:[&_[cmdk-group-heading]]:px-2 setu:[&_[cmdk-group-heading]]:font-medium setu:[&_[cmdk-group]]:px-2 setu:[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 setu:[&_[cmdk-input-wrapper]_svg]:h-5 setu:[&_[cmdk-input-wrapper]_svg]:w-5 setu:[&_[cmdk-input]]:h-12 setu:[&_[cmdk-item]]:px-2 setu:[&_[cmdk-item]]:py-3 setu:[&_[cmdk-item]_svg]:h-5 setu:[&_[cmdk-item]_svg]:w-5">
                    {children}
                </Command>
            </DialogContent>
        </Dialog>
    );
}
CommandDialog.displayName = "CommandDialog";

/**
 * CommandInput Component
 *
 * Input field for searching commands.
 */
function CommandInput({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Input>) {
    return (
        <div
            data-slot="command-input-wrapper"
            className="setu:flex setu:h-9 setu:items-center setu:gap-2 setu:!border-b setu:px-3"
        >
            <SearchIcon className="setu:size-4 setu:shrink-0 setu:opacity-50" />
            <CommandPrimitive.Input
                data-slot="command-input"
                className={cn(
                    "setu:placeholder:text-muted-foreground setu:flex setu:h-10 setu:w-full setu:rounded-md setu:bg-transparent setu:py-3 setu:text-sm setu:outline-hidden setu:disabled:cursor-not-allowed setu:disabled:opacity-50",
                    className
                )}
                {...props}
            />
        </div>
    );
}
CommandInput.displayName = "CommandInput";

/**
 * CommandList Component
 *
 * Container for command items.
 */
function CommandList({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.List>) {
    return (
        <CommandPrimitive.List
            data-slot="command-list"
            className={cn("setu:max-h-[300px] setu:scroll-py-1 setu:overflow-x-hidden setu:overflow-y-auto", className)}
            {...props}
        />
    );
}
CommandList.displayName = "CommandList";

/**
 * CommandEmpty Component
 *
 * Displayed when no commands match the search.
 */
function CommandEmpty({ ...props }: React.ComponentProps<typeof CommandPrimitive.Empty>) {
    return (
        <CommandPrimitive.Empty
            data-slot="command-empty"
            className="setu:py-6 setu:text-center setu:text-sm"
            {...props}
        />
    );
}
CommandEmpty.displayName = "CommandEmpty";

/**
 * CommandGroup Component
 *
 * Groups related command items.
 */
function CommandGroup({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Group>) {
    return (
        <CommandPrimitive.Group
            data-slot="command-group"
            className={cn(
                "setu:text-foreground setu:[&_[cmdk-group-heading]]:text-muted-foreground setu:overflow-hidden setu:p-1 setu:[&_[cmdk-group-heading]]:px-2 setu:[&_[cmdk-group-heading]]:py-1.5 setu:[&_[cmdk-group-heading]]:text-xs setu:[&_[cmdk-group-heading]]:font-medium",
                className
            )}
            {...props}
        />
    );
}
CommandGroup.displayName = "CommandGroup";

/**
 * CommandSeparator Component
 *
 * Visual separator between command groups.
 */
function CommandSeparator({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Separator>) {
    return (
        <CommandPrimitive.Separator
            data-slot="command-separator"
            className={cn("setu:!bg-border setu:-mx-1 setu:h-px", className)}
            {...props}
        />
    );
}
CommandSeparator.displayName = "CommandSeparator";

/**
 * CommandItem Component
 *
 * Individual command item in the list.
 */
function CommandItem({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Item>) {
    return (
        <CommandPrimitive.Item
            data-slot="command-item"
            className={cn(
                "setu:data-[selected=true]:bg-accent setu:data-[selected=true]:text-accent-foreground setu:[&_svg:not([class*='text-'])]:text-muted-foreground setu:relative setu:flex setu:cursor-default setu:items-center setu:gap-2 setu:rounded-sm setu:px-2 setu:py-1.5 setu:text-sm setu:outline-hidden setu:select-none setu:data-[disabled=true]:pointer-events-none setu:data-[disabled=true]:opacity-50 setu:[&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        />
    );
}
CommandItem.displayName = "CommandItem";

/**
 * CommandShortcut Component
 *
 * Keyboard shortcut display for command items.
 */
function CommandShortcut({ className, ...props }: React.ComponentProps<"span">) {
    return (
        <span
            data-slot="command-shortcut"
            className={cn("setu:text-muted-foreground setu:ml-auto setu:text-xs setu:tracking-widest", className)}
            {...props}
        />
    );
}
CommandShortcut.displayName = "CommandShortcut";

export {
    Command,
    CommandDialog,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandShortcut,
    CommandSeparator,
};
